/**
 * The words half of the capability manifest: for every capability id, what it
 * is called, what it does, what a business uses it for, the question that
 * reveals the need, who can make it today, a draft part that builds it (or one
 * that tries it and is refused), and the closest fit when it cannot be made.
 *
 * `satisfies Record<CapabilityId, CapabilityWords>`: a new id is a compile
 * error until its words are written, and a removed one cannot keep stale
 * words. Not exported from the package on its own; `buildManifest` is the one
 * way in.
 *
 * Rules for the words: plain English a shop owner reads, sentence case,
 * RecordOS's own vocabulary (record, list, entry, field, card, link), and no
 * product's name but RecordOS's (manifest.test.ts enforces it). `reach.setup`
 * is what `authorProblems` accepts today; the phase that widens the draft
 * flips an entry, with its tests. Every `example` uses keys prefixed by its
 * entry, so all of them can sit in one draft together.
 */
import { COMPOSE_LIMITS } from '../compose';
import type { SetupDraftField, SetupDraftRecord } from '../compose-author';
import { LEDGER_ACCOUNT_TYPES as ACCOUNT_TYPES, type PostingPattern } from '../ledger/patterns';
import type { SpecRoleSource } from '../spec/posting';
import type { CapabilityId } from './ids';
import type { StudioCardLeaf, StudioRole } from '../setup-draft/draft';
import type { CapabilityReach, CapabilityWords, SetupDraftFragment, StudioDraftFragment } from './manifest';

/* --------------------------------------------------------------- helpers */

/** Made by a setup today. */
const made = (os: readonly string[], edit: 'yes' | 'no' = 'no'): CapabilityReach => ({ setup: 'yes', edit, os, screen: true });
/** Made only once the workspace exists: by OS, or on a screen. */
const after = (os: readonly string[], screen = true, edit: 'yes' | 'no' = 'no'): CapabilityReach => ({ setup: 'no', edit, os, screen });
/** Made by nothing yet. */
const nothing: CapabilityReach = { setup: 'no', edit: 'no', os: [], screen: false };

const f = (key: string, label: string, type: SetupDraftField['type'], over: Partial<SetupDraftField> = {}): SetupDraftField => ({
  key,
  label,
  type,
  ...over,
});

/** A record whose entries are written each day, headed by a text field. */
const rec = (key: string, name: string, fields: SetupDraftField[], over: Partial<SetupDraftRecord> = {}): SetupDraftRecord => ({
  key,
  name,
  entryNoun: 'entry',
  kind: 'record',
  direction: 'none',
  fields: [f('title', 'Title', 'text'), ...fields],
  headlineFieldKey: 'title',
  ...over,
});

/** A list other records point at, headed by its name. */
const list = (key: string, name: string, fields: SetupDraftField[] = [], over: Partial<SetupDraftRecord> = {}): SetupDraftRecord => ({
  key,
  name,
  entryNoun: 'row',
  kind: 'directory',
  direction: 'none',
  fields: [f('name', 'Name', 'text'), ...fields],
  headlineFieldKey: 'name',
  ...over,
});

/** A record with a day and an amount it totals. */
const sales = (key: string, name: string, fields: SetupDraftField[] = [], over: Partial<SetupDraftRecord> = {}): SetupDraftRecord =>
  rec(key, name, [f('date', 'Date', 'date'), f('amount', 'Amount', 'money'), ...fields], {
    direction: 'in',
    dateFieldKey: 'date',
    measureFieldKey: 'amount',
    ...over,
  });

const one = (...records: SetupDraftRecord[]): SetupDraftFragment => ({ records });

/**
 * A money record and the rule that counts both sides of each entry (L4 of
 * docs/plans/ledger.md): an Amount, a Date, the fields `more` adds, and a
 * rule of `pattern` whose accounts are read by system key, so the example
 * needs no Accounts list.
 */
const counted = (pattern: PostingPattern, roles: Readonly<Record<string, SpecRoleSource>>, more: SetupDraftField[] = [], over: Partial<SetupDraftRecord> = {}): SetupDraftFragment =>
  one(sales(`money_${pattern}`, 'Money', more, { posting: { pattern, roles: { amount: { fieldKey: 'amount' }, ...roles } }, ...over }));
const key = (systemKey: string): SpecRoleSource => ({ systemKey });

/** One field of a type with no engine, cast past the draft's type. */
const fieldOfType = (prefix: string, type: string): SetupDraftFragment =>
  one(rec(`${prefix}_record`, 'Totals', [f('worked', 'Worked out', type as SetupDraftField['type'])]));

const NO_DRAFT_ROLES = 'A setup draft holds records only; it has no roles, access or invites.';
const NO_DRAFT_PLACE = 'A setup draft has no part for this.';
/** A printed document is made after Build: OS drafts its template, and the record's screen issues it. */
const AFTER_BUILD_DOCUMENT: CapabilityReach = { setup: 'no', edit: 'no', os: ['draft_template'], screen: true };
const DOCUMENT_NOT_IN_DRAFT = 'A setup draft has no part for a printed document; it is made after Build.';
const AFTER_BUILD_DOCUMENT_FIT = 'after Build, ask OS for the receipt';
const SETUP_HOLDS_NAME: SetupDraftFragment = { records: [] };

/** A formula field over this row. */
const formula = (key: string, label: string, result: 'number' | 'money' | 'quantity' | 'text' | 'boolean' | 'date', expr: NonNullable<SetupDraftField['formula']>['expr']): SetupDraftField =>
  f(key, label, 'formula', { formula: { result, expr, onMissing: 'blank' } });

const field = (fieldKey: string) => ({ kind: 'field' as const, fieldKey });

/* ----------------------------------------------------------------- words */

/* ------------------------------------------- parts only the studio draft holds */

const MODE = f('mode', 'Paid by', 'select', {
  options: [
    { key: 'cash', label: 'Cash' },
    { key: 'upi', label: 'UPI' },
    { key: 'credit', label: 'Credit' },
  ],
});
/** A record and a role over it. */
const withRole = (prefix: string, name: string, access: StudioRole['access']): StudioDraftFragment => ({
  records: [sales(`${prefix}_sales`, 'Sales')],
  roles: [{ key: prefix, name, people: [], base: 'member', permissions: [], access }],
});

/** A card total of one record's measure. */
const measureOf = (record: string): StudioCardLeaf => ({ kind: 'agg', agg: 'sum', record, of: { kind: 'measure' } });

/** The file an import example reads: any uuid; the store checks the row, never the judge. */
const IMPORT_SOURCE = '5b0f0a6e-8c1d-4a2b-9e3f-1d2c3b4a5e6f';

export const CAPABILITY_WORDS = {
  /* ------------------------------------------------------- the workspace */
  'workspace.name': {
    name: 'Workspace name',
    what: "The business's name, shown on every screen",
    use: 'Sharma Traders; Smile Dental',
    status: 'shipped',
    reach: made(['propose_business']),
    example: SETUP_HOLDS_NAME,
  },
  'workspace.currency': {
    name: 'Currency',
    what: 'The money every amount is in',
    use: 'Rupees for a shop in Pune; dirhams in Dubai',
    also: ['rupees', 'paisa', 'currency'],
    status: 'shipped',
    reach: made(['propose_business']),
    example: SETUP_HOLDS_NAME,
  },
  'workspace.financial_year': {
    name: 'Financial year',
    what: 'The month the year of profit, closing and tax starts in',
    use: 'April to March in India; January to December in the UAE',
    also: ['financial year', 'fiscal year', 'FY', 'year end'],
    status: 'shipped',
    reach: made(['propose_business']),
    example: SETUP_HOLDS_NAME,
  },
  'workspace.tax': {
    name: 'Tax',
    what: 'Whether bills carry tax, and which: GST, one VAT or GST, or sales tax',
    use: 'GST registered for a shop with a GSTIN; VAT at 5% in Dubai',
    also: ['gst', 'vat', 'tax', 'registered'],
    status: 'shipped',
    reach: made(['propose_business']),
    example: SETUP_HOLDS_NAME,
  },
  // Step 13.3 of docs/plans/ledger.md (L60): one set of words, `explainMoney`, said by the Build sheet, the Dashboard and OS.
  'workspace.how_money_works': {
    name: 'How money works here',
    what: 'Four lines on how this workspace keeps money: what each entry was for, both sides counted, To sort, and the year, tax and approvals OS set',
    use: 'Shown once before Build and once on the Dashboard after it; OS says the same lines when asked',
    ask: 'How does money work here?',
    also: ['how does money work', 'how is money counted', 'paisa kaise', 'hisaab kaise'],
    status: 'shipped',
    reach: { setup: 'no', edit: 'no', os: [], screen: true },
    unexpressible: 'Worked out from the records, the year, the tax and the approvals, never written into a setup.',
  },
  'workspace.logo': {
    name: 'Logo',
    what: 'A picture for the workspace',
    use: 'On bills and beside the name',
    status: 'shipped',
    reach: after([]),
    unexpressible: 'A logo is picked from the device on a screen, never said in a sentence.',
  },
  'workspace.timezone': {
    name: 'Time zone',
    what: 'Which day today is, for daily totals',
    use: "Today's sales close at midnight where the shop is",
    status: 'shipped',
    reach: { setup: 'no', edit: 'no', os: [], screen: false },
    unexpressible: "Each device's own clock decides the day; the workspace keeps no zone.",
  },
  'workspace.language': {
    name: 'Language',
    what: 'The language OS speaks in a conversation',
    use: 'Hindi, Tamil, English and more',
    also: ['bhasha', 'hindi', 'language'],
    status: 'shipped',
    reach: { setup: 'no', edit: 'no', os: [], screen: true },
    unexpressible: 'The language is chosen per conversation, not kept on the workspace.',
  },

  /* ----------------------------------------------------------- the data */
  'field.type.text': {
    name: 'Text',
    what: 'A short line of words',
    use: 'An item name; a vehicle number',
    status: 'shipped',
    reach: made(['propose_fields'], 'yes'),
    example: one(rec('type_text', 'Deliveries', [f('vehicle', 'Vehicle number', 'text')])),
  },
  'field.type.long_text': {
    name: 'Note',
    what: 'A longer piece of writing',
    use: 'Remarks on a visit',
    also: ['remarks', 'note'],
    status: 'shipped',
    reach: made(['propose_fields'], 'yes'),
    example: one(rec('type_long_text', 'Visits', [f('remarks', 'Remarks', 'long_text')])),
  },
  'field.type.number': {
    name: 'Number',
    what: 'A figure that is not money and not a count',
    use: 'A meter reading; a room number',
    status: 'shipped',
    reach: made(['propose_fields'], 'yes'),
    example: one(rec('type_number', 'Readings', [f('reading', 'Meter reading', 'number')])),
  },
  'field.type.money': {
    name: 'Money',
    what: 'An amount, in the workspace currency',
    use: 'A fee; a rate; what was paid',
    also: ['amount', 'paisa', 'rupees', 'price'],
    status: 'shipped',
    reach: made(['propose_fields'], 'yes'),
    example: one(rec('type_money', 'Fees', [f('fee', 'Fee', 'money')])),
  },
  'field.type.quantity': {
    name: 'Quantity',
    what: 'A count or a measure, with a unit if wanted',
    use: 'Litres of milk; crates; kilos',
    also: ['qty', 'kitna', 'litres', 'kg'],
    status: 'shipped',
    reach: made(['propose_fields'], 'yes'),
    example: one(rec('type_quantity', 'Milk', [f('litres', 'Litres', 'quantity')])),
  },
  'field.type.date': {
    name: 'Date',
    what: 'A day',
    use: 'The visit date; a due date',
    also: ['tareekh', 'din', 'due date'],
    status: 'shipped',
    reach: made(['propose_fields'], 'yes'),
    example: one(rec('type_date', 'Bills', [f('due', 'Due date', 'date')])),
  },
  'field.type.datetime': {
    name: 'Date and time',
    what: 'A day with its time',
    use: 'A booking slot; when a truck left',
    status: 'shipped',
    reach: made(['propose_fields'], 'yes'),
    example: one(rec('type_datetime', 'Bookings', [f('slot', 'Slot', 'datetime')])),
  },
  'field.type.boolean': {
    name: 'Yes or no',
    what: 'A tick that is on or off',
    use: 'Delivered; paid in full',
    status: 'shipped',
    reach: made(['propose_fields'], 'yes'),
    example: one(rec('type_boolean', 'Orders', [f('delivered', 'Delivered', 'boolean')])),
  },
  'field.type.select': {
    name: 'Choice',
    what: 'One of a few fixed choices',
    use: 'Cash, UPI or card; small, medium or large',
    ask: 'Is it always one of a few fixed answers?',
    status: 'shipped',
    reach: made(['propose_fields'], 'yes'),
    example: one(
      rec('type_select', 'Payments', [
        f('mode', 'Paid by', 'select', { options: [{ key: 'cash', label: 'Cash' }, { key: 'upi', label: 'UPI' }, { key: 'card', label: 'Card' }] }),
      ]),
    ),
  },
  'field.type.multi_select': {
    name: 'Several from a list',
    what: 'Several rows of one list on one entry',
    use: 'The services of one salon visit',
    ask: 'Can one visit have several services?',
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(
      list('type_multi_select_services', 'Services'),
      rec('type_multi_select_visits', 'Visits', [f('services', 'Services', 'multi_select', { targetRecordKey: 'type_multi_select_services' })]),
    ),
  },
  'field.type.reference': {
    name: 'Pick from a list',
    what: 'One row of a list, or one entry of another record, picked by name',
    use: 'The customer of a sale; the item sold',
    ask: 'Do you pick the customer from people you already know?',
    also: ['customer', 'party', 'grahak'],
    status: 'shipped',
    reach: made(['propose_fields'], 'yes'),
    example: one(
      list('type_reference_customers', 'Customers'),
      rec('type_reference_sales', 'Sales', [f('customer', 'Customer', 'reference', { targetRecordKey: 'type_reference_customers' })]),
    ),
  },
  'field.type.attachment': {
    name: 'Photo or file',
    what: 'A photo or a file kept on the entry',
    use: 'A photo of the bill',
    also: ['photo', 'bill photo', 'receipt'],
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(rec('type_attachment', 'Lab bills', [f('photo', 'Bill photo', 'attachment')])),
  },
  'field.type.phone': {
    name: 'Phone',
    what: 'A number to call',
    use: "A customer's phone",
    also: ['mobile', 'number', 'phone'],
    status: 'shipped',
    reach: made(['propose_fields'], 'yes'),
    example: one(list('type_phone', 'Customers', [f('phone', 'Phone', 'phone')])),
  },
  'field.type.location': {
    name: 'Place on the map',
    what: 'A spot on the map',
    use: 'The site of a job; where a delivery went',
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(rec('type_location', 'Site visits', [f('site', 'Site', 'location')])),
  },
  'field.type.formula': {
    name: 'Worked out',
    what: 'A value worked out from other fields, never typed',
    use: 'Balance = fee less what was paid',
    ask: 'Is any number worked out from the others?',
    also: ['hisaab', 'total', 'balance'],
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(
      rec('type_formula', 'Visits', [
        f('fee', 'Fee', 'money'),
        f('paid', 'Paid', 'money'),
        formula('balance', 'Balance', 'money', { kind: 'calc', op: 'subtract', left: field('fee'), right: field('paid') }),
      ]),
    ),
  },
  'field.type.rollup': {
    name: 'Total of other entries',
    what: 'A total, a count, or the smallest or largest number or date of the entries of another record that pick this one, kept as a field',
    use: "A booking's room bill: the orders added to it; a member's current expiry: the latest End date of their memberships",
    ask: 'Does each one add up the entries that belong to it?',
    also: ['room bill', 'bill', 'running total'],
    status: 'shipped',
    // OS adds one to a record that exists too (PR 37): the owner names the total after setup.
    reach: made(['propose_fields']),
    example: one(
      rec('type_rollup_bookings', 'Bookings', [
        f('room_charge', 'Room charge', 'money'),
        f('room_bill', 'Room Bill', 'rollup', {
          rollup: {
            agg: 'sum',
            overRecordKey: 'type_rollup_orders',
            viaFieldKey: 'booking',
            ofFieldKey: 'total_amount',
            where: [{ fieldKey: 'payment_status', optionKey: 'room_bill' }],
          },
        }),
      ]),
      rec('type_rollup_orders', 'Guest Service Orders', [
        f('booking', 'Booking', 'reference', { targetRecordKey: 'type_rollup_bookings' }),
        f('total_amount', 'Total Amount', 'money'),
        f('payment_status', 'Payment Status', 'select', {
          options: [
            { key: 'room_bill', label: 'Added to Room Bill' },
            { key: 'paid', label: 'Paid' },
          ],
        }),
      ]),
    ),
  },
  'field.type.lookup': {
    name: 'Read through',
    what: 'A value read off another record, kept as a field',
    use: "The customer's area on each sale",
    status: 'no_engine',
    reach: nothing,
    attempt: fieldOfType('type_lookup', 'lookup'),
    cannotYet: { need: 'A field that copies a value from another record', fit: 'a worked-out field that reads it through the reference' },
  },
  'field.type.lines': {
    name: 'Lines',
    what: 'Many rows inside one entry, each with its own values',
    use: 'The medicines on a bill; the rooms of a stay',
    ask: 'Does one bill have many medicines?',
    also: ['items', 'line items', 'bill items', 'saman', 'maal'],
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(
      rec('lines_sales', 'Daily sales', [
        f('medicines', 'Medicines', 'lines', {
          lines: {
            noun: 'medicine',
            nouns: 'medicines',
            fields: [
              f('item', 'Item', 'text'),
              f('qty', 'Qty', 'quantity'),
              f('rate', 'Rate', 'money'),
              formula('line_total', 'Amount', 'money', { kind: 'calc', op: 'multiply', left: field('qty'), right: field('rate') }),
            ],
          },
        }),
        formula('total', 'Total', 'money', { kind: 'agg', op: 'sum', lines: 'medicines', of: 'line_total' }),
      ]),
    ),
  },
  'field.type.series': {
    name: 'Number series',
    what: 'A number each entry is given in turn when it is saved, or typed from a paper voucher',
    use: 'A voucher number for each job, counted separately for each machine',
    ask: 'Does each one carry its own number, like a voucher or a receipt?',
    also: ['voucher no', 'serial no', 'order no', 'parchi no', 'kramank'],
    status: 'shipped',
    // Made on Customize and by OS on a record that exists (Phase 8 of
    // docs/plans/workbook-map.md). Setup draws it by code from a workbook
    // map's series column (8.8): the model's stages are never offered it.
    reach: made(['propose_fields']),
    example: one(
      rec('type_series_jobs', 'Roller jobs', [
        f('machine', 'Machine', 'select', {
          options: [
            { key: 'jcb', label: 'JCB' },
            { key: 'case', label: 'CASE' },
          ],
        }),
        f('voucher_no', 'Voucher No', 'series', { series: { per: 'machine', once: true } }),
      ]),
    ),
  },
  'field.option.required': {
    name: 'Must be filled',
    what: 'The entry cannot be saved without it',
    use: 'The amount of a sale',
    status: 'shipped',
    reach: made(['propose_fields'], 'yes'),
    example: one(rec('option_required', 'Sales', [f('amount', 'Amount', 'money', { required: true })])),
  },
  'field.option.sticky': {
    name: 'Keeps the last value',
    what: 'The next entry opens with the value just used',
    use: "Today's route; the same customer again",
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(rec('option_sticky', 'Rounds', [f('route', 'Route', 'text', { sticky: true })])),
  },
  'field.option.help': {
    name: 'Help line',
    what: 'A short line under a field',
    use: '"Fee less what was paid"',
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(rec('option_help', 'Visits', [f('fee', 'Fee', 'money', { help: 'What the visit cost' })])),
  },
  'field.option.shape': {
    name: 'How a field looks',
    what: 'The control a field is filled with',
    use: 'Quick amounts; a stepper; choice buttons',
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(rec('option_shape', 'Tips', [f('tip', 'Tip', 'money', { quickAmounts: ['10', '20', '50'] })])),
  },
  'field.option.option_sign_tone': {
    name: 'Choice that counts in or out',
    what: 'Each choice says whether money comes in or goes out, and the colour it wears',
    use: 'Gave or got on a ledger; present in green',
    also: ['gave', 'got', 'diya', 'liya', 'jama', 'naam'],
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(
      sales(
        'option_sign_tone',
        'Ledger',
        [
          f('way', 'Gave or got', 'select', {
            options: [
              { key: 'got', label: 'Got', sign: 1, tone: 'positive' },
              { key: 'gave', label: 'Gave', sign: -1, tone: 'negative' },
            ],
          }),
        ],
        { direction: 'both', signFieldKey: 'way' },
      ),
    ),
  },
  'field.option.filter': {
    name: 'Choice narrowed by another',
    what: 'A list that shows only the rows that fit another field',
    use: 'Sub-category picked within the category',
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(
      list('option_filter_categories', 'Categories'),
      list('option_filter_subs', 'Sub-categories', [f('category', 'Category', 'reference', { targetRecordKey: 'option_filter_categories' })]),
      rec('option_filter_expenses', 'Expenses', [
        f('category', 'Category', 'reference', { targetRecordKey: 'option_filter_categories' }),
        f('sub', 'Sub-category', 'reference', { targetRecordKey: 'option_filter_subs', filter: { field: 'category', equals: 'category' } }),
      ]),
    ),
  },
  'field.option.max_chosen': {
    name: 'Most that can be picked',
    what: 'How many rows one entry may pick from a list',
    use: 'Up to three toppings',
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(
      list('option_max_chosen_toppings', 'Toppings'),
      rec('option_max_chosen_orders', 'Orders', [f('toppings', 'Toppings', 'multi_select', { targetRecordKey: 'option_max_chosen_toppings', maxChosen: 3 })]),
    ),
  },
  'field.option.starts_with': {
    name: 'Starting value',
    what: 'The value a new entry opens with, which can be typed over',
    use: "The customer's usual rate",
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(
      rec('option_starts_with', 'Milk', [f('rate', 'Rate', 'money', { startsWith: { result: 'money', expr: { kind: 'const', value: 60 }, onMissing: 'blank' } })]),
    ),
  },
  'field.option.show_when': {
    name: 'Shown only sometimes',
    what: 'A field that shows only when another answer calls for it',
    use: 'Cheque number, only when paid by cheque',
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(
      rec('option_show_when', 'Payments', [
        f('by_cheque', 'Paid by cheque', 'boolean'),
        f('cheque', 'Cheque number', 'text', { showWhen: { result: 'boolean', expr: field('by_cheque'), onMissing: 'blank' } }),
      ]),
    ),
  },
  'field.option.editable_when': {
    name: 'Locked sometimes',
    what: 'A field that can be changed only when a yes-or-no rule over the typed fields and one hop allows it, and still takes its starting value',
    use: 'The end date, open only when the plan has no length (a custom plan)',
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(
      rec('option_editable_when', 'Memberships', [
        f('custom', 'Custom plan', 'boolean'),
        f('price', 'Price', 'money', {
          startsWith: { result: 'money', expr: { kind: 'const', value: 1000 }, onMissing: 'blank' },
          editableWhen: { result: 'boolean', expr: field('custom'), onMissing: 'blank' },
        }),
      ]),
    ),
  },
  'field.option.open_row': {
    name: 'Starts with the open row',
    what: 'A pick that starts with the latest row still open for something the entry already picked; the person may pick another',
    use: 'Fuel bought on the road lands on the trip that vehicle is running',
    also: ['open trip', 'running job', 'current'],
    // Step 9.2 of docs/plans/ledger.md: what the Link target match_open_row meant, as a default.
    status: 'shipped',
    reach: { setup: 'yes', edit: 'no', os: [], screen: false },
    example: one(
      list('option_open_row_vehicles', 'Vehicles'),
      rec('option_open_row_trips', 'Trips', [
        f('vehicle', 'Vehicle', 'reference', { targetRecordKey: 'option_open_row_vehicles' }),
        f('status', 'Status', 'select', { options: [{ key: 'running', label: 'Running' }, { key: 'done', label: 'Done' }] }),
      ]),
      rec('option_open_row_fuel', 'Fuel', [
        f('vehicle', 'Vehicle', 'reference', { targetRecordKey: 'option_open_row_vehicles' }),
        f('trip', 'Trip', 'reference', {
          targetRecordKey: 'option_open_row_trips',
          openRow: { by: 'vehicle', match: 'vehicle', status: { field: 'status', open: ['running'] } },
        }),
      ]),
    ),
  },
  'field.option.once': {
    name: 'Starting value, filled once',
    what: 'A starting value worked out when the entry or the line is added, never again; only it may read a worked-out field, or a field of the row above a line',
    use: 'A payment line whose Paid starts with the Balance above it',
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(
      rec('option_once', 'Sales', [
        f('qty', 'Qty', 'quantity'),
        f('rate', 'Rate', 'money'),
        formula('total', 'Total', 'money', { kind: 'calc', op: 'multiply', left: field('qty'), right: field('rate') }),
        f('paid', 'Paid', 'money', {
          startsWith: { result: 'money', expr: field('total'), onMissing: 'blank' },
          startsOnce: true,
        }),
      ]),
    ),
  },
  'field.option.lines_start': {
    name: 'Lines to start with',
    what: 'The empty lines a new entry opens with, 0 to 3',
    use: 'A bill that opens with one payment line',
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(
      rec('option_lines_start', 'Bills', [
        f('amount', 'Amount', 'money'),
        f('payments', 'Payments', 'lines', {
          lines: { noun: 'payment', nouns: 'payments', fields: [f('paid', 'Paid', 'money')], start: 1 },
        }),
      ]),
    ),
  },
  'record.record': {
    name: 'Record',
    what: 'A place where entries are written each day',
    use: 'Sales, expenses, visits, trips',
    also: ['register', 'bahi', 'khata'],
    status: 'shipped',
    reach: made(['propose_record'], 'yes'),
    example: one(rec('record_record', 'Expenses', [f('amount', 'Amount', 'money')])),
    attempt: {
      records: Array.from({ length: COMPOSE_LIMITS.records + 1 }, (_, at) => rec(`record_over_${at + 1}`, `Record ${at + 1}`, [])),
    },
  },
  'record.list': {
    name: 'List',
    what: 'Names other records pick from',
    use: 'Customers, items, staff, payment modes',
    ask: 'Do you pick the same customers or items again and again?',
    also: ['customers', 'items', 'staff', 'master'],
    status: 'shipped',
    reach: made(['propose_record'], 'yes'),
    example: one(list('record_list', 'Items')),
  },
  // The shipped way of a balance on a party's row (W11 of docs/plans/workbook-map.md, the middle way):
  // it took the place of not_yet.balance_on_party. Owner, 29 Oct 2026: a card reads the stored figure over the list's rows.
  'record.list_money': {
    name: 'Money on a list, worked out',
    what: "Billed, received and pending on each customer's row, worked out from the records that hold the money and kept up to date on every save",
    use: "Each customer's pending on their row; the customers whose pending is at least 1; a card of pending by customer and of the pending total, read off each customer's row",
    ask: 'Do you want to see what each customer was billed, has paid and still owes, on their row?',
    also: ['balance', 'baki', 'khata', 'udhaar', 'pending', 'outstanding', 'owes', 'billed', 'received'],
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(
      list('record_list_money_customers', 'Customers', [
        f('billed', 'Billed', 'rollup', { rollup: { agg: 'sum', overRecordKey: 'record_list_money_jobs', viaFieldKey: 'customer', ofFieldKey: 'total' } }),
        f('received', 'Received', 'rollup', { rollup: { agg: 'sum', overRecordKey: 'record_list_money_jobs', viaFieldKey: 'customer', ofFieldKey: 'paid' } }),
        f('pending', 'Pending', 'formula', {
          formula: {
            result: 'money',
            onMissing: 'zero',
            expr: { kind: 'calc', op: 'subtract', left: { kind: 'field', fieldKey: 'billed' }, right: { kind: 'field', fieldKey: 'received' } },
          },
        }),
      ]),
      rec('record_list_money_jobs', 'Jobs', [
        f('customer', 'Customer', 'reference', { targetRecordKey: 'record_list_money_customers' }),
        f('total', 'Total', 'money'),
        f('paid', 'Paid now', 'money'),
      ]),
    ),
  },
  'record.direction': {
    name: 'Money in or out',
    what: 'Whether entries add to the total, take from it, or only count',
    use: 'Sales add; expenses take; attendance counts',
    status: 'shipped',
    reach: made(['propose_record'], 'yes'),
    example: one(rec('record_direction', 'Expenses', [f('amount', 'Amount', 'money')], { direction: 'out' })),
  },
  'record.icon': {
    name: 'Icon',
    what: 'The picture a record wears, from what it holds',
    use: 'Stock, people, money',
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(list('record_icon', 'Stock items', [], { icon: 'stock' })),
  },
  'record.entry_noun': {
    name: 'Word for one entry',
    what: "The business's own word for one entry",
    use: 'A visit, a bill, a trip',
    status: 'shipped',
    reach: made(['propose_record']),
    example: one(rec('record_entry_noun', 'Trips', [], { entryNoun: 'trip' })),
  },
  'record.seed_rows': {
    name: 'Starting rows',
    what: 'The rows a list starts with',
    use: 'Cash, UPI, card; expense categories',
    status: 'shipped',
    reach: made([]),
    example: one(
      list('record_seed_rows', 'Payment modes', [], {
        seedRows: [{ values: [{ fieldKey: 'name', value: 'Cash' }] }, { values: [{ fieldKey: 'name', value: 'UPI' }] }],
      }),
    ),
  },
  'record.bindings': {
    name: 'What a record answers',
    what: 'Which field names an entry, which is its day, which amount it totals',
    use: 'A sale is named by its customer, dated by its day, totalled by its amount',
    status: 'shipped',
    reach: made(['propose_record']),
    example: one(sales('record_bindings', 'Sales', [], { focusFieldKey: 'amount' })),
  },
  'record.archive': {
    name: 'Leave a record out',
    what: 'Put a record away with its entries, never deleting them',
    use: 'A record the business no longer keeps',
    status: 'shipped',
    reach: made([], 'yes'),
    studioExample: { records: [rec('record_archive', 'Old sales', [])] },
  },
  'record.rename': {
    name: 'Rename a record',
    what: "Change a record's name",
    use: 'Sales becomes Counter sales',
    status: 'shipped',
    reach: made([]),
    studioExample: { records: [rec('record_rename', 'Counter sales', [])] },
  },
  'record.must_be': {
    name: 'Must be',
    what: 'A yes-or-no rule a field or the whole entry must keep, with the words shown when it does not ({placeholders} name values); it stops the save (block) or only warns (warn), and may read worked-out fields, totals over the lines and one hop',
    use: 'What is received cannot be more than the amount; the quantities on the lines add up to no more than Total items',
    status: 'shipped',
    // OS adds one after Build with propose_fields (Phase 5); a setup draft carries it too (Phase 6).
    reach: made(['propose_fields']),
    example: one(
      rec('must_be', 'Bills', [
        f('amount', 'Amount', 'money'),
        f('received', 'Received', 'money', {
          mustBe: [
            {
              formula: {
                result: 'boolean',
                expr: { kind: 'if', left: field('received'), op: 'lte', right: field('amount'), then: { kind: 'const', value: true }, otherwise: { kind: 'const', value: false } },
                onMissing: 'blank',
              },
              message: 'Received {Received} is more than the amount {Amount}.',
              level: 'block',
            },
          ],
        }),
      ]),
    ),
  },
  'record.kit': {
    name: 'Kit',
    what: 'A tested piece RecordOS puts in with code, whole: its lines, worked-out fields, defaults and rules, set by a few knobs',
    use: 'Payments on a sale: each payment a line, what was received and what is still due, never paid past the amount',
    also: ['kit', 'payments', 'paid in parts', 'membership'],
    status: 'shipped',
    // A setup draft carries it as a kit change (Phase 6); OS puts an attached kit on a record that exists with add_kit (Phase 8).
    reach: { setup: 'yes', edit: 'no', os: ['add_kit'], screen: false },
    example: one(
      rec('kit_payments', 'Sales', [
        f('amount', 'Amount', 'money'),
        f('payments', 'Payments', 'lines', {
          lines: { noun: 'payment', nouns: 'payments', start: 1, fields: [f('paid', 'Paid', 'money')] },
        }),
      ]),
    ),
  },
  'formula.piece.field': {
    name: 'A field on this entry',
    what: 'A worked-out value reads a field of the same entry',
    use: 'Fee, to work out the balance',
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(rec('formula_field', 'Visits', [f('fee', 'Fee', 'money'), formula('copy', 'Charged', 'money', field('fee'))])),
  },
  'formula.piece.hop': {
    name: 'A field through a pick',
    what: 'A worked-out value reads a field of the row a pick points at',
    use: "The item's rate on a sale",
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(
      list('formula_hop_items', 'Items', [f('pack', 'Pack size', 'number')]),
      rec('formula_hop_sales', 'Sales', [
        f('item', 'Item', 'reference', { targetRecordKey: 'formula_hop_items' }),
        formula('pack', 'Pack size', 'number', { kind: 'hop', via: 'item', fieldKey: 'pack' }),
      ]),
    ),
  },
  'formula.piece.const': {
    name: 'A fixed number',
    what: 'A worked-out value uses a number that never changes',
    use: 'Tax at 18 in a hundred',
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(
      rec('formula_const', 'Bills', [
        f('amount', 'Amount', 'money'),
        formula('tax', 'Tax', 'money', { kind: 'calc', op: 'multiply', left: field('amount'), right: { kind: 'const', value: 0.18 } }),
      ]),
    ),
  },
  'formula.piece.calc': {
    name: 'Add, take, times, divide',
    what: 'A worked-out value adds, takes away, multiplies or divides',
    use: 'Litres times rate',
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(
      rec('formula_calc', 'Milk', [
        f('litres', 'Litres', 'quantity'),
        f('rate', 'Rate', 'money'),
        formula('amount', 'Amount', 'money', { kind: 'calc', op: 'multiply', left: field('litres'), right: field('rate') }),
      ]),
    ),
  },
  'formula.piece.round': {
    name: 'Round',
    what: 'A worked-out value rounded to so many places',
    use: 'A rate rounded to two places',
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(
      rec('formula_round', 'Splits', [
        f('total', 'Total', 'money'),
        formula('share', 'Each share', 'money', {
          kind: 'round',
          decimals: 2,
          of: { kind: 'calc', op: 'divide', left: field('total'), right: { kind: 'const', value: 3 } },
        }),
      ]),
    ),
  },
  'formula.piece.if': {
    name: 'If this, then that',
    what: 'A worked-out value that depends on a test',
    use: 'Late fee only when past the due day',
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(
      rec('formula_if', 'Rent', [
        f('days_late', 'Days late', 'number'),
        formula('late_fee', 'Late fee', 'money', {
          kind: 'if',
          left: field('days_late'),
          op: 'gt',
          right: { kind: 'const', value: 0 },
          then: { kind: 'const', value: 100 },
          otherwise: { kind: 'const', value: 0 },
        }),
      ]),
    ),
  },
  'formula.piece.days_between': {
    name: 'Days between',
    what: 'The number of days from one date to another',
    use: 'Nights of a stay',
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(
      rec('formula_days_between', 'Stays', [
        f('check_in', 'Check in', 'date'),
        f('check_out', 'Check out', 'date'),
        formula('nights', 'Nights', 'number', { kind: 'days_between', from: field('check_in'), to: field('check_out') }),
      ]),
    ),
  },
  'formula.piece.concat': {
    name: 'Join words',
    what: 'Words of several fields joined into one',
    use: 'Room and guest name in one line',
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(
      rec('formula_concat', 'Stays', [
        f('room', 'Room', 'text'),
        f('guest', 'Guest', 'text'),
        formula('label', 'Room and guest', 'text', { kind: 'concat', parts: [field('room'), { kind: 'const', value: ' · ' }, field('guest')] }),
      ]),
    ),
  },
  'formula.piece.coalesce': {
    name: 'Or else',
    what: 'The first of several values that is filled',
    use: "Today's rate, or else the usual one",
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(
      rec('formula_coalesce', 'Milk', [
        f('rate', 'Rate today', 'money'),
        f('usual', 'Usual rate', 'money'),
        formula('charged', 'Charged', 'money', { kind: 'coalesce', of: [field('rate'), field('usual')] }),
      ]),
    ),
  },
  'formula.piece.agg': {
    name: 'Total of the lines',
    what: 'A total over the lines of an entry, or over the rows it picked',
    use: 'The bill total from its lines',
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(
      list('formula_agg_services', 'Services', [f('minutes', 'Minutes', 'number')]),
      rec('formula_agg_visits', 'Visits', [
        f('services', 'Services', 'multi_select', { targetRecordKey: 'formula_agg_services' }),
        formula('minutes', 'Minutes', 'number', { kind: 'agg', op: 'sum', lines: 'services', of: 'minutes' }),
      ]),
    ),
  },
  'formula.piece.date_add': {
    name: 'A date moved on',
    what: 'A date moved on by so many days, weeks, months or years: the amount and the unit typed, fixed, or read through a pick (a plan\'s Length and Unit, whose choices are days, weeks, months, years); Valid till gives the last day inside the period',
    use: 'The end date of a membership: Start date plus the plan\'s length, valid till',
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(
      rec('formula_date_add', 'Memberships', [
        f('start', 'Start date', 'date'),
        f('months', 'Months', 'number'),
        formula('ends', 'Valid till', 'date', {
          kind: 'date_add',
          date: field('start'),
          amount: field('months'),
          unit: { kind: 'const', value: 'months' },
          validTill: true,
        }),
      ]),
    ),
  },
  'formula.piece.today': {
    name: 'Today',
    what: "The workspace's day, read when a form opens or saves: in a starting value, Shown only sometimes, Locked sometimes and Must be, never in a worked-out field, because nothing would work it out again the next day",
    use: 'A renewal that starts today; a reason that shows only when a bill is past due',
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(
      rec('formula_today', 'Bills', [
        f('due', 'Due date', 'date'),
        f('late_reason', 'Why late', 'text', {
          showWhen: {
            result: 'boolean',
            onMissing: 'blank',
            expr: {
              kind: 'if',
              left: field('due'),
              op: 'lt',
              right: { kind: 'today' },
              then: { kind: 'const', value: true },
              otherwise: { kind: 'const', value: false },
            },
          },
        }),
      ]),
    ),
  },

  /* -------------------------------------------------------- the surface */
  'surface.form': {
    name: 'Form',
    what: 'One entry written at a time',
    use: 'Almost every record',
    status: 'shipped',
    reach: made(['propose_record'], 'yes'),
    example: one(rec('surface_form', 'Expenses', [f('amount', 'Amount', 'money')])),
  },
  'surface.sheet': {
    name: 'Sheet',
    what: 'One line per row of a list, one tap each, for one day',
    use: 'Staff attendance; a delivery round',
    ask: 'Is attendance marked for everyone at once?',
    also: ['attendance', 'hazri', 'register'],
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(
      list('surface_sheet_staff', 'Staff'),
      rec(
        'surface_sheet_attendance',
        'Attendance',
        [
          f('date', 'Date', 'date'),
          f('staff', 'Staff', 'reference', { targetRecordKey: 'surface_sheet_staff' }),
          f('mark', 'Mark', 'select', { options: [{ key: 'present', label: 'Present', tone: 'positive' }, { key: 'absent', label: 'Absent', tone: 'negative' }] }),
        ],
        { headlineFieldKey: 'staff', dateFieldKey: 'date', capture: { mode: 'sheet', overFieldKey: 'staff', cellFieldKey: 'mark', quickOptionKey: 'present' } },
      ),
    ),
  },
  'surface.catalogue': {
    name: 'Catalogue',
    what: 'Items tapped off a menu into one entry',
    use: 'A restaurant order',
    status: 'planned',
    reach: nothing,
    unexpressible: 'A setup draft has no catalogue surface.',
    cannotYet: { need: 'Tapping items off a menu', fit: 'lines on the entry, each picking an item from a list' },
  },
  'surface.counter': {
    name: 'Counter',
    what: 'A running count tapped up and down',
    use: 'Footfall at the door',
    status: 'planned',
    reach: nothing,
    unexpressible: 'A setup draft has no counter surface.',
    cannotYet: { need: 'A tap counter', fit: 'a record with a quantity that starts at one, and a card of the day total' },
  },
  'surface.timer': {
    name: 'Timer',
    what: 'A clock started and stopped on an entry',
    use: 'A salon chair; hours on a job',
    status: 'planned',
    reach: nothing,
    unexpressible: 'A setup draft has no timer surface.',
    cannotYet: { need: 'A start and stop timer', fit: 'a start time and an end time, and the hours worked out' },
  },

  /* ---------------------------------------------------- the arrangement */
  'arrangement.sections': {
    name: 'Sections',
    what: 'Groups of fields on the form, which can fold',
    use: 'Patient details, then treatment, then payment',
    status: 'shipped',
    reach: made(['propose_layout']),
    example: one(
      rec(
        'arrangement_sections',
        'Visits',
        [f('patient', 'Patient', 'text'), f('fee', 'Fee', 'money'), f('notes', 'Notes', 'long_text')],
        {
          layout: [
            { title: 'Visit', style: 'plain', fieldKeys: ['title', 'patient'] },
            { title: 'Payment', style: 'card', fieldKeys: ['fee'] },
            { title: 'More', style: 'folded', fieldKeys: ['notes'] },
          ],
        },
      ),
    ),
  },
  'arrangement.steps': {
    name: 'Steps',
    what: 'A form filled page by page',
    use: 'A long admission form',
    status: 'planned',
    reach: nothing,
    unexpressible: 'A setup draft has no steps.',
    cannotYet: { need: 'A form in pages', fit: 'sections, with the later ones folded' },
  },
  'arrangement.tabs': {
    name: 'Tabs',
    what: 'A form split into tabs',
    use: 'Details and documents of one job',
    status: 'planned',
    reach: nothing,
    unexpressible: 'A setup draft has no tabs.',
    cannotYet: { need: 'A form in tabs', fit: 'sections on one form' },
  },

  /* ------------------------------------------------------ the lifecycle */
  'lifecycle.statuses': {
    name: 'Statuses',
    what: 'The stages an entry moves through',
    use: 'Booked, checked in, checked out',
    also: ['status', 'stage', 'pending', 'done'],
    status: 'planned',
    reach: nothing,
    unexpressible: 'A setup draft has no statuses.',
    cannotYet: { need: 'Stages an entry moves through', fit: 'a choice field for the stage, and a card of how many are at each' },
  },
  'lifecycle.approval': {
    name: 'Approval',
    what: 'An entry that waits for workspace members to approve it, step by step, before it counts',
    use: 'Expenses above ₹5,000 go to the manager, and above ₹50,000 to the owner as well',
    also: ['approve', 'approval', 'sign off', 'send back', 'manager'],
    // Phase 11 of docs/plans/ledger.md: the chain is set in the record's
    // settings; OS approves or sends back what waits for the person. Step 12.1
    // (L55): a setup writes the chain from words, on the record whose entries
    // wait, decided by the writer's manager or by the owner or an admin.
    status: 'shipped',
    reach: { setup: 'yes', edit: 'no', os: ['propose_approval_decision', 'propose_approval_chain'], screen: true },
    example: one(
      rec('lifecycle_approval', 'Expenses', [f('amount', 'Amount', 'money')], {
        direction: 'out',
        measureFieldKey: 'amount',
        approvalChain: { conditions: { every: false, above: 5000, byRole: false }, steps: [{ who: { kind: 'owner_or_admin' }, above: null }] },
      }),
    ),
  },
  'lifecycle.reminders': {
    name: 'Reminders',
    what: 'A reminder raised from an entry on a day',
    use: 'Rent due on the fifth; follow up a customer',
    also: ['reminder', 'yaad', 'alert', 'due'],
    status: 'planned',
    reach: nothing,
    unexpressible: 'A setup draft has no reminders.',
    // Not a task: a task from an entry on a day ships (lifecycle.tasks), and a call a few days before a plan ends is one (Iron Temple Gym, 30 Oct: noted as not possible).
    cannotYet: { need: 'A reminder sent by itself on a day, as a notification or a message', fit: 'a task from the entry, due a number of days from one of its dates' },
  },

  'lifecycle.tasks': {
    name: 'Tasks from entries',
    what: 'A task for the owner, the writer or a member, raised when an entry is saved or approved, due a number of days from one of its dates',
    use: 'Three days before a membership ends, the owner is asked to call the member',
    also: ['task', 'to do', 'follow up', 'call', 'remind me'],
    // Step 9.3 of docs/plans/ledger.md: what the Link pattern reminder meant, as a task rule on the record.
    // Step 9.7: OS proposes one after Build; the record's settings show and take one off, and the Dashboard shows the tasks.
    status: 'shipped',
    reach: { setup: 'yes', edit: 'no', os: ['propose_task_rule'], screen: false },
    example: one(
      rec('lifecycle_tasks', 'Memberships', [f('ends', 'End date', 'date')], {
        tasks: [{ when: [{ fieldKey: 'ends', op: 'filled' }], assignee: { kind: 'owner' }, title: 'Call about the plan that ends {ends}', due: { fromFieldKey: 'ends', days: -3 } }],
      }),
    ),
  },

  /* ----------------------------------------------------------- the view */
  'view.list': {
    name: 'List view',
    what: 'All entries, newest first, grouped by day',
    use: 'Every sale of the day, newest first',
    status: 'shipped',
    reach: made([]),
    example: one(sales('view_list', 'Sales')),
  },
  'view.summary': {
    name: 'Cards on top',
    what: 'The cards on top of a record',
    use: "Today's total, this month against last",
    status: 'shipped',
    reach: made(['propose_card']),
    example: one(
      sales('view_summary', 'Sales', [], {
        tiles: [
          { label: 'Today', card: 'insight.stat', agg: 'sum', scope: 'day' },
          { label: 'This month', card: 'insight.delta', agg: 'sum', scope: 'month' },
        ],
      }),
    ),
  },
  'view.stored_options': {
    name: 'Saved list options',
    what: 'A saved field order, hidden fields and a filter on the list (hidden_fields, field_order, component_override)',
    use: 'Hide the cost price on the list',
    status: 'stored_not_drawn',
    reach: nothing,
    unexpressible: 'A setup draft writes the list view itself, and no screen reads its saved options.',
    cannotYet: { need: 'A saved order or hidden fields on the list', fit: 'sections on the form, and OS shows a view for now' },
  },
  'view.view_for_now': {
    name: 'A view for now',
    what: 'The list shown a certain way for now, with a way back',
    use: 'Only UPI sales this week',
    status: 'shipped',
    reach: after(['set_view'], false),
    unexpressible: 'A view for now is set in a workspace, never stored by a setup.',
  },
  'view.board': {
    name: 'Board',
    what: 'Entries in columns by their stage',
    use: 'Jobs by status',
    status: 'planned',
    reach: nothing,
    unexpressible: 'A setup draft has no board view.',
    cannotYet: { need: 'A board of entries by stage', fit: 'a choice field for the stage, and a breakdown card by it' },
  },
  'view.calendar': {
    name: 'Calendar',
    what: 'Entries on a calendar by their day',
    use: 'Bookings of the month',
    also: ['calendar', 'booking'],
    status: 'planned',
    reach: nothing,
    unexpressible: 'A setup draft has no calendar view.',
    cannotYet: { need: 'A calendar of bookings', fit: 'the list view grouped by day, and a card of the coming week' },
  },
  'view.resource_grid': {
    name: 'Resource grid',
    what: 'Rooms or chairs down the side, days across the top',
    use: 'Which room is booked on which night',
    status: 'planned',
    reach: nothing,
    unexpressible: 'A setup draft has no grid view.',
    cannotYet: { need: 'A grid of rooms by night', fit: 'a list of rooms, stays that pick one, and a card of nights by room' },
  },

  /* ------------------------------------------------------- the documents */
  'document.template': {
    name: 'Printed document',
    what: 'A receipt, bill or invoice printed from an entry, laid out once',
    use: "The receipt a guest takes at check-out",
    also: ['receipt', 'bill', 'invoice', 'pdf', 'rasid', 'parchi'],
    status: 'shipped',
    reach: AFTER_BUILD_DOCUMENT,
    unexpressible: DOCUMENT_NOT_IN_DRAFT,
    cannotYet: { need: 'A printed receipt or invoice drawn in the setup', fit: AFTER_BUILD_DOCUMENT_FIT },
  },
  'document.series': {
    name: 'Document numbers',
    what: 'Each printed document numbered in its own series, with no gaps',
    use: 'Receipt numbers R-001, R-002 and on',
    also: ['receipt number', 'invoice number', 'bill number', 'series'],
    status: 'shipped',
    reach: AFTER_BUILD_DOCUMENT,
    unexpressible: DOCUMENT_NOT_IN_DRAFT,
    cannotYet: { need: 'Receipt or invoice numbers drawn in the setup', fit: AFTER_BUILD_DOCUMENT_FIT },
  },
  'document.issue': {
    name: 'Issued document',
    what: 'A printed document issued from an entry and kept as it was issued',
    use: 'The bill a guest was given, kept with the booking',
    also: ['issue', 'send bill', 'share pdf'],
    status: 'shipped',
    reach: AFTER_BUILD_DOCUMENT,
    unexpressible: DOCUMENT_NOT_IN_DRAFT,
    cannotYet: { need: 'A receipt issued for each entry, drawn in the setup', fit: AFTER_BUILD_DOCUMENT_FIT },
  },
  'document.payment_qr': {
    name: 'Payment QR',
    what: 'A QR code on a printed document that pays its total or balance',
    use: 'The balance due, scanned and paid from the bill',
    also: ['qr', 'scan to pay', 'payment code'],
    status: 'shipped',
    reach: AFTER_BUILD_DOCUMENT,
    unexpressible: DOCUMENT_NOT_IN_DRAFT,
    cannotYet: { need: 'A payment QR on a receipt, drawn in the setup', fit: AFTER_BUILD_DOCUMENT_FIT },
  },

  /* -------------------------------------------------------- the insight */
  'insight.card.stat': {
    name: 'Number card',
    what: 'One number: a total, a count, an average',
    use: "Today's sales",
    status: 'shipped',
    reach: made(['propose_card']),
    example: one(sales('card_stat', 'Sales', [], { tiles: [{ label: 'Today', card: 'insight.stat', agg: 'sum', scope: 'day' }] })),
  },
  'insight.card.delta': {
    name: 'Against last time',
    what: 'This period against the one before',
    use: 'This month against last month',
    status: 'shipped',
    reach: made(['propose_card']),
    example: one(sales('card_delta', 'Sales', [], { tiles: [{ label: 'This month', card: 'insight.delta', agg: 'sum', scope: 'month' }] })),
  },
  'insight.card.progress': {
    name: 'Progress card',
    what: 'This period as a bar against the one before',
    use: 'Collections this week so far',
    status: 'shipped',
    reach: made(['propose_card']),
    example: one(sales('card_progress', 'Collections', [], { tiles: [{ label: 'This week', card: 'insight.progress', agg: 'sum', scope: 'week' }] })),
  },
  'insight.card.trend': {
    name: 'Trend line',
    what: 'One number a day, as a line',
    use: 'Sales over the last two weeks',
    status: 'shipped',
    reach: made(['propose_card']),
    example: one(sales('card_trend', 'Sales', [], { tiles: [{ label: 'Two weeks', card: 'insight.trend', agg: 'sum', days: 14 }] })),
  },
  'insight.card.bars': {
    name: 'Bars by day',
    what: 'One number a day, as bars',
    use: 'Milk collected each day this week',
    status: 'shipped',
    reach: made(['propose_card']),
    example: one(sales('card_bars', 'Milk', [], { tiles: [{ label: 'This week', card: 'insight.bars', agg: 'sum', days: 7 }] })),
  },
  'insight.card.breakdown': {
    name: 'Breakdown',
    what: 'One number per group, largest first',
    use: 'Sales by item; expenses by category',
    status: 'shipped',
    reach: made(['propose_card']),
    example: one(
      sales('card_breakdown', 'Sales', [f('mode', 'Paid by', 'select', { options: [{ key: 'cash', label: 'Cash' }, { key: 'upi', label: 'UPI' }] })], {
        tiles: [{ label: 'By mode', card: 'insight.breakdown', agg: 'sum', scope: 'month', groupByFieldKey: 'mode' }],
      }),
    ),
  },
  'insight.agg': {
    name: 'Total, count, average',
    what: 'A card adds up, counts, averages, or takes the smallest or largest',
    use: 'Average bill size',
    status: 'shipped',
    reach: made(['propose_card']),
    example: one(sales('insight_agg', 'Bills', [], { tiles: [{ label: 'Average bill', card: 'insight.stat', agg: 'avg', scope: 'month' }] })),
  },
  'insight.scope': {
    name: 'Period',
    what: 'The day, week, month, year or all time a card reads, on the entry\'s date or on another date field of the row',
    use: 'This week; check-outs this week by Check-out date',
    status: 'shipped',
    reach: made(['propose_card']),
    example: one(sales('insight_scope', 'Sales', [], { tiles: [{ label: 'This year', card: 'insight.stat', agg: 'sum', scope: 'year' }] })),
  },
  'insight.where': {
    name: 'Only some entries',
    what: 'A card counts only the entries that meet a few conditions',
    use: 'Only cash sales',
    status: 'shipped',
    reach: made(['propose_card']),
    example: one(
      sales('insight_where', 'Sales', [f('mode', 'Paid by', 'select', { options: [{ key: 'cash', label: 'Cash' }, { key: 'upi', label: 'UPI' }] })], {
        tiles: [{ label: 'Cash today', card: 'insight.stat', agg: 'sum', scope: 'day', where: [{ fieldKey: 'mode', op: 'is', optionKey: 'cash' }] }],
      }),
    ),
  },
  'insight.where_list': {
    name: 'Any of, none of',
    what: 'A card condition that matches any of several choices, or none of them',
    use: 'Cash or UPI sales',
    status: 'shipped',
    reach: made(['propose_card']),
    studioExample: {
      records: [sales('insight_where_list_sales', 'Sales', [MODE])],
      boardCards: [
        {
          key: 'insight_where_list',
          label: 'Cash or UPI today',
          card: 'insight.stat',
          scope: 'day',
          total: { ...measureOf('insight_where_list_sales'), where: [{ field: 'mode', op: 'in', value: ['cash', 'upi'] }] },
        },
      ],
    },
  },
  'insight.date_condition': {
    name: 'Date conditions',
    what: 'A date asked against today: is today, before or after today, in the next or last few days, on or before or after a date. Cards, the table filter, rules and OS share them, and the day is the workspace\'s',
    use: 'Members who expired; check-outs due in the next 7 days',
    status: 'shipped',
    reach: made(['propose_card', 'list_entries', 'total', 'set_view']),
    studioExample: {
      records: [sales('insight_date_condition_members', 'Members', [f('expiry', 'Expiry', 'date')])],
      boardCards: [
        {
          key: 'insight_date_condition',
          label: 'Expired',
          card: 'insight.stat',
          scope: 'all',
          total: { kind: 'agg', agg: 'rows', record: 'insight_date_condition_members', where: [{ field: 'expiry', op: 'before_today' }] },
        },
      ],
    },
  },
  'insight.group_by': {
    name: 'By group',
    what: 'A card split by a field',
    use: 'Sales by item',
    status: 'shipped',
    reach: made(['propose_card']),
    example: one(
      sales('insight_group_by', 'Expenses', [f('kind', 'Kind', 'select', { options: [{ key: 'rent', label: 'Rent' }, { key: 'power', label: 'Power' }] })], {
        direction: 'out',
        tiles: [{ label: 'By kind', card: 'insight.breakdown', agg: 'sum', scope: 'month', groupByFieldKey: 'kind', limit: 5 }],
      }),
    ),
  },
  'insight.via': {
    name: 'Through a pick',
    what: 'A card condition or group read through the row a pick points at',
    use: "Sales by the customer's area",
    status: 'shipped',
    reach: made(['propose_card']),
    studioExample: {
      records: [sales('insight_via_sales', 'Sales', [f('customer', 'Customer', 'reference', { targetRecordKey: 'insight_via_customers' })]), list('insight_via_customers', 'Customers', [f('area', 'Area', 'text')])],
      boardCards: [
        {
          key: 'insight_via',
          label: 'Sold in Kothrud',
          card: 'insight.stat',
          scope: 'month',
          total: { ...measureOf('insight_via_sales'), where: [{ field: 'area', op: 'is', value: 'Kothrud', via: 'customer' }] },
        },
      ],
    },
  },
  'insight.row_recipe': {
    name: 'Worked out per entry',
    what: 'A card that totals a value worked out per entry',
    use: 'Total of litres times rate',
    status: 'shipped',
    reach: after(['propose_card']),
    unexpressible: 'A setup card totals one field; it holds no recipe.',
  },
  'insight.cross_record': {
    name: 'Across records',
    what: 'Totals joined across records, on the Dashboard',
    use: 'What each customer owes: credit less payments, by customer',
    ask: 'Do you want to see what each customer owes across bills and payments?',
    also: ['udhaar', 'baki', 'outstanding', 'owes', 'lena dena'],
    status: 'shipped',
    reach: made(['propose_card']),
    studioExample: {
      records: [sales('insight_cross_sales', 'Sales'), sales('insight_cross_costs', 'Costs', [], { direction: 'out' })],
      boardCards: [
        {
          key: 'insight_cross_record',
          label: 'Left this month',
          card: 'insight.stat',
          scope: 'month',
          total: { kind: 'calc', op: 'subtract', left: measureOf('insight_cross_sales'), right: measureOf('insight_cross_costs') },
        },
      ],
    },
  },
  'insight.dashboard': {
    name: 'Dashboard card',
    what: 'A card on the Dashboard, across the workspace',
    use: 'Cash in hand today',
    status: 'shipped',
    reach: made(['propose_card']),
    studioExample: {
      records: [sales('insight_dashboard_sales', 'Sales')],
      boardCards: [{ key: 'insight_dashboard', label: 'Sold today', card: 'insight.stat', scope: 'day', total: measureOf('insight_dashboard_sales') }],
    },
  },
  'insight.rows_count': {
    name: 'Count of entries',
    what: 'How many entries, rather than how many filled a field',
    use: 'Visits this month',
    status: 'shipped',
    reach: after(['propose_card']),
    unexpressible: 'A setup count counts the filled values of one field.',
  },

  /* ----------------------------------------------------- the automation */
  'money.pattern.money_in': {
    name: 'Money in',
    what: 'Money that comes in, and what it was for: Cash goes up and an income goes up',
    use: 'A day\'s cash sales, rent received, a loan taken',
    ask: 'What money comes in, and what is it for?',
    also: ['income', 'aamdani', 'jama'],
    status: 'shipped',
    reach: made(['propose_entry']),
    example: counted('money_in', { account: key('sales'), paid_to: key('cash') }),
  },
  'money.pattern.money_out': {
    name: 'Money out',
    what: 'Money that goes out, and what it was for: Cash goes down and an expense, For home or a loan paid back goes up',
    use: 'Rent paid, tea for the shop, ₹500 taken for home',
    ask: 'What money goes out, and what is it for?',
    also: ['expense', 'kharcha', 'for home'],
    status: 'shipped',
    reach: made(['propose_entry']),
    example: counted('money_out', { account: key('to_sort'), paid_from: key('cash') }, [], { direction: 'out' }),
  },
  'money.pattern.sale': {
    name: 'Sale',
    what: 'A sale: what the customer owes goes up and Sales goes up; a part paid now, a discount, a commission and a cost each count too',
    use: 'A roller job of ₹5,000, ₹2,000 paid by UPI, 5% to the operator',
    also: ['bill', 'invoice', 'job'],
    status: 'shipped',
    reach: made(['propose_entry']),
    example: counted('sale', { income: key('sales'), paid_now: { fieldKey: 'paid' }, paid_to: key('cash') }, [f('paid', 'Paid now', 'money')]),
  },
  'money.pattern.purchase': {
    name: 'Purchase',
    what: 'A purchase: what you owe the supplier goes up and an expense goes up; a part paid now counts too',
    use: 'Stock bought on credit, half paid in cash',
    also: ['bought', 'kharid'],
    status: 'shipped',
    reach: made(['propose_entry']),
    example: counted('purchase', { expense: key('stock_loss'), paid_now: { fieldKey: 'paid' }, paid_to: key('cash') }, [f('paid', 'Paid now', 'money')], { direction: 'out' }),
  },
  'money.pattern.receipt': {
    name: 'Payment received',
    what: 'A customer pays what they owe: Cash goes up and what they owe goes down. When the payment picks the customer, its Amount starts with what they still owe; the person may change it',
    use: 'Kabir pays ₹2,800 of his fees',
    also: ['payment received', 'jama', 'udhaar paid back', 'amount due', 'prefill amount'],
    status: 'shipped',
    reach: made(['propose_entry']),
    example: counted('receipt', { paid_to: key('cash') }),
  },
  'money.pattern.payment': {
    name: 'Payment made',
    what: 'You pay a supplier what you owe: Cash goes down and what you owe goes down. When the payment picks the supplier, its Amount starts with what you still owe them; the person may change it',
    use: 'The milk supplier\'s bill paid at month end',
    also: ['paid supplier'],
    status: 'shipped',
    reach: made(['propose_entry']),
    example: counted('payment', { paid_from: key('cash') }, [], { direction: 'out' }),
  },
  'money.pattern.charge': {
    name: 'Charge',
    what: 'What a customer is charged, before they pay: what they owe goes up and an income goes up, with a discount if any',
    use: 'A month\'s tuition fee of ₹2,000 for Asha, 10% off',
    also: ['fee', 'dues', 'bill'],
    status: 'shipped',
    reach: made(['propose_entry']),
    example: counted('charge', { income: key('sales') }),
  },
  'money.pattern.concession': {
    name: 'Concession',
    what: 'Money let off: what the customer owes goes down and Discounts given goes up, for a period if named',
    use: 'Meera\'s May fee reduced by ₹500',
    also: ['discount', 'credit note', 'maafi'],
    status: 'shipped',
    reach: made(['propose_entry']),
    example: counted('concession', {}, [], { direction: 'out' }),
  },
  'money.pattern.transfer': {
    name: 'Transfer',
    what: 'Money moved from one place to another, counting as neither income nor expense',
    use: 'Cash with the operator handed to the office (Office Jama), cash put in the bank',
    also: ['deposit', 'office jama', 'handover'],
    status: 'shipped',
    reach: made(['propose_entry']),
    example: counted('transfer', { from: key('cash'), to: key('bank') }, [], { direction: 'none' }),
  },
  'money.pattern.refund': {
    name: 'Refund',
    what: 'Money given back: Cash goes down and what the customer owes, or Sales, comes back',
    use: 'A returned item paid back in cash',
    also: ['return', 'wapas'],
    status: 'shipped',
    reach: made(['propose_entry']),
    example: counted('refund', { paid_from: key('cash') }, [], { direction: 'out' }),
  },
  'money.pattern.adjustment': {
    name: 'Adjustment',
    what: 'A correction between two accounts, always with a reason',
    use: 'Cash counted ₹40 short at closing',
    also: ['correction', 'short', 'excess'],
    status: 'shipped',
    reach: made(['propose_entry', 'propose_adjustment']),
    example: counted('adjustment', { account: key('stock_loss'), against: key('cash'), reason: { fieldKey: 'title' } }, [], { direction: 'none' }),
  },
  'money.pattern.opening_balance': {
    name: 'Opening balance',
    what: 'What an account or a customer held on the first day, set against Opening balances',
    use: 'Cash in the drawer when the shop starts using RecordOS, an old khata balance',
    also: ['opening', 'old balance', 'purana hisaab'],
    status: 'shipped',
    reach: made(['propose_entry']),
    example: counted('opening_balance', { account: key('cash') }, [], { direction: 'none' }),
  },
  'money.pattern.bill_lines': {
    name: 'Bill with lines',
    what: 'A bill of several lines, each counted to its own income or expense, with a discount and a round off',
    use: 'A tax invoice of three items',
    also: ['invoice lines', 'items'],
    status: 'shipped',
    reach: made(['propose_entry']),
    example: one(
      sales('bills', 'Bills', [
        {
          key: 'items',
          label: 'Items',
          type: 'lines',
          lines: { noun: 'item', nouns: 'items', fields: [f('item', 'Item', 'text'), f('line_amount', 'Amount', 'money')] },
        },
      ], {
        posting: { pattern: 'bill_lines', roles: { lines: { fieldKey: 'items', each: { amount: { fieldKey: 'line_amount' }, account: { systemKey: 'sales' } } } } },
      }),
    ),
  },
  'money.pattern.tax_payment': {
    name: 'Tax paid',
    what: 'The tax of a period paid: tax collected goes down, tax you can claim back is set off, and the bank pays the rest',
    use: 'The month\'s GST paid',
    also: ['GST paid', 'VAT paid', 'tax payment'],
    // Step 10.7: the Tax payment form is made at Build for a workspace that charges tax, and suggests the month's figures.
    status: 'shipped',
    reach: after(['propose_entry']),
    unexpressible: 'The Tax payment form is made at Build for a workspace that charges tax; a setup draft never writes it.',
  },
  'money.pattern.quantity': {
    name: 'Stock that moves',
    what: 'Stock that comes in, goes out or is wasted: each entry counts its item\'s quantity, in the item\'s own unit, and a pack is turned into that unit (2 peti of 12 is 24 pcs); the stock list shows On hand and Wastage',
    use: 'Milk collected from farmers adds litres on hand, milk sold takes them away, spoilt milk shows in Wastage',
    also: ['stock', 'inventory', 'on hand', 'maal', 'wastage', 'godown'],
    // Phase 9 (9.1): a rule's quantity part, beside its money or alone, posts quantity lines in the same save.
    status: 'shipped',
    reach: made(['propose_entry']),
    example: one(
      list('items', 'Items', [f('unit', 'Unit', 'select', { options: [{ key: 'pcs', label: 'pcs' }, { key: 'kg', label: 'kg' }] })], { stockItems: { unitFieldKey: 'unit' } }),
      rec('stock_in', 'Stock received', [f('date', 'Date', 'date'), f('item', 'Item', 'reference', { targetRecordKey: 'items' }), f('qty', 'Quantity', 'quantity')], {
        headlineFieldKey: 'item',
        dateFieldKey: 'date',
        posting: { pattern: 'quantity', quantity: { move: 'in', item: 'item', qty: 'qty' } },
      }),
    ),
  },
  'money.pattern.charge_plan': {
    name: 'Charge plan',
    what: 'A plan on a list that charges every active customer each month, quarter or year, one entry each in the Charges record',
    use: 'Every student\'s monthly fee written on the first of the month',
    also: ['monthly fee', 'subscription', 'recurring'],
    // Phase 5: the plan runs, and the Charges record shows the next run; the plan is set up on the list, not in a draft.
    status: 'shipped',
    reach: after([]),
    unexpressible: 'A charge plan sits on a list beside its Charges record; a setup draft has no part for it.',
    cannotYet: { need: 'Set up a charge plan while drafting the setup', fit: 'after Build, ask OS to charge the list each month' },
  },
  /* ------------------------------------------------- how money is kept */
  // Step 12.6 of docs/plans/ledger.md: each part a person meets has its own
  // entry, so OS explains a refusal by the part it is about. The words are
  // business words (L19): never the code's names for how money is kept.
  'money.rule': {
    name: 'Counts in money',
    what: 'A record whose entries count both sides by themselves: what each was for, and where the money went or came from',
    use: 'Every Money out takes Cash down and puts Rent or Tea up, on its save',
    ask: 'Does this record move money, or only note an amount?',
    also: ['counts in money', 'both sides', 'hisaab', 'not in money totals'],
    status: 'shipped',
    reach: made(['propose_entry', 'propose_entry_change']),
    example: one(
      sales('money_rule_sales', 'Cash sales', [], {
        posting: { pattern: 'money_in', roles: { amount: { fieldKey: 'amount' }, account: key('sales'), paid_to: key('cash') } },
      }),
    ),
  },
  'money.accounts': {
    name: 'Accounts',
    what: 'One list of where money is and what it is for, each row with its type: money you have, money you owe, owner\'s money, income or expense',
    use: 'Cash, Bank, Sales, Rent, For home and To sort',
    also: ['accounts', 'categories', 'heads', 'khata'],
    status: 'shipped',
    reach: made(['read_account']),
    example: {
      records: [
        list('money_accounts_list', 'Accounts', [f('type', 'Type', 'select', { options: ACCOUNT_TYPES.map((type) => ({ key: type, label: type })) })], {
          entryNoun: 'account',
          accounts: { typeFieldKey: 'type' },
          seedRows: [
            { values: [{ fieldKey: 'name', value: 'Cash' }, { fieldKey: 'type', value: 'asset' }], account: { systemKey: 'cash' } },
            { values: [{ fieldKey: 'name', value: 'Rent' }, { fieldKey: 'type', value: 'expense' }] },
          ],
        }),
        sales('money_accounts_out', 'Money out', [f('what_for', 'What was it for?', 'reference', { targetRecordKey: 'money_accounts_list' })], {
          direction: 'out',
          posting: { pattern: 'money_out', roles: { amount: { fieldKey: 'amount' }, account: { fieldKey: 'what_for' }, paid_from: key('cash') } },
        }),
      ],
    },
  },
  'money.category': {
    name: 'Category',
    what: 'What an entry was for, picked from the Accounts list; a new one typed in the picker becomes a row of its own',
    use: 'Tea, Rent or Diesel on a Money out',
    ask: 'What was it for?',
    also: ['category', 'kis liye', 'what for', 'expense head'],
    status: 'shipped',
    reach: after(['propose_entry', 'propose_category']),
    unexpressible: 'A setup drafts its categories as rows of the Accounts list; one more is typed in the picker after Build.',
  },
  'money.to_sort': {
    name: 'To sort',
    what: 'Money whose purpose is not known yet waits here, out of profit, until someone picks what it was for; a month cannot close while it holds money',
    use: '₹1,000 paid out, not sure what for',
    also: ['not sure', 'to sort', 'pata nahi', 'unknown expense'],
    status: 'shipped',
    reach: after(['propose_entry_change', 'propose_sort']),
    unexpressible: 'A row the money kit adds to the Accounts list; a setup draft never makes it alone.',
  },
  'money.match': {
    name: 'Paid against a charge',
    what: 'Each payment is set against the oldest open charge of the same customer by itself; a person may move it to another charge',
    use: 'Asha\'s ₹1,800 pays her April fee',
    also: ['against dues', 'settle', 'which bill', 'adjust payment'],
    status: 'shipped',
    reach: after(['propose_payment_move']),
    unexpressible: 'Worked out on every save from the charges and payments; nothing in a setup draft holds it.',
  },
  'money.statement': {
    name: 'Statement',
    what: 'A customer\'s or supplier\'s charges, payments and balance on their row, with the periods paid and due',
    use: 'What Kavya Clinic owes, and for which months',
    ask: 'Who owes you, and how much?',
    also: ['statement', 'dues', 'kitna baki', 'baaki', 'outstanding', 'owes'],
    status: 'shipped',
    reach: after(['total', 'read_statement']),
    unexpressible: 'Worked out from the entries that name the customer; a setup draft never writes it.',
  },
  'money.charge_run': {
    name: 'Charge run',
    what: 'The charges a plan writes for every active customer each period, with Run now and Skip this period on the Charges record',
    use: 'Every student\'s fee written on the first of the month, by itself',
    also: ['run now', 'skip this period', 'monthly charges', 'auto charge'],
    status: 'shipped',
    reach: after(['propose_charge_run']),
    unexpressible: 'A run is what a charge plan does after Build; a setup draft has no part for it.',
  },
  'money.party_period': {
    name: 'Joined and left',
    what: 'The days a customer was active, from the day they joined to the day they left, so a plan charges only those periods',
    use: 'Meera joins on 1 May; a member who leaves stops being charged',
    also: ['joined', 'left', 'active', 'membership dates'],
    status: 'shipped',
    reach: after(['propose_entry_change']),
    unexpressible: 'Written from the Joined and Left dates of each row after Build; a setup draft holds no dates of a row.',
  },
  'money.close': {
    name: 'Close a month',
    what: 'Every entry on or before the day closed stays as it is; it waits while To sort holds money or entries wait for approval',
    use: 'Close March once the cash is counted',
    also: ['close month', 'month end', 'lock month', 'closing'],
    status: 'shipped',
    reach: after(['propose_month_close']),
    unexpressible: 'Owners and admins close a month in the Money section of Workspace details, after Build.',
  },
  'money.correct': {
    name: 'Correct',
    what: 'An entry in a closed month is never changed; Correct writes a new entry today that undoes it and counts the right figures, and each links to the other',
    use: 'Fix a February bill after February is closed',
    also: ['correct', 'fix a closed entry', 'galti', 'mistake'],
    status: 'shipped',
    reach: after(['propose_correction']),
    unexpressible: 'Made from the entry page of an entry in a closed month, after Build.',
  },
  'money.see_money': {
    name: 'See money',
    what: 'The right to see balances, statements, the Accounts list and the money cards; owners and admins hold it, and a role can be given it',
    use: 'A cashier records sales but does not see profit',
    also: ['see money', 'see profit', 'hide balances', 'who sees money'],
    status: 'shipped',
    reach: after(['propose_role', 'propose_role_change', 'read_money']),
    unexpressible: NO_DRAFT_ROLES,
  },
  'money.accountant_view': {
    name: 'Accountant view',
    what: 'Cards only an accountant needs, every account side by side and the tax summary, with the export pack, turned on in Workspace details',
    use: 'The year-end figures for the accountant',
    also: ['accountant', 'CA', 'year end figures', 'export for accountant'],
    status: 'shipped',
    reach: after([]),
    unexpressible: 'Turned on in the Money section of Workspace details, after Build.',
  },

  /* --------------------------------------------------------- the people */
  'people.group.records': {
    name: 'Rights over records',
    what: 'Who may see, add, change, delete and download entries',
    use: 'Counter staff add sales but never delete them',
    status: 'shipped',
    reach: after(['propose_role', 'propose_role_change']),
    unexpressible: NO_DRAFT_ROLES,
  },
  'people.group.insights': {
    name: 'Rights over cards',
    what: 'Who may see and change cards and the Dashboard',
    use: 'Only the owner sees the profit card',
    status: 'shipped',
    reach: after(['propose_role', 'propose_role_change']),
    unexpressible: NO_DRAFT_ROLES,
  },
  'people.group.team': {
    name: 'Rights over the team',
    what: 'Who may invite, remove people, and change roles and access',
    use: 'The manager invites new staff',
    status: 'shipped',
    reach: after(['propose_role', 'propose_role_change']),
    unexpressible: NO_DRAFT_ROLES,
  },
  'people.group.workspace': {
    name: 'Rights over the workspace',
    what: 'Who may change the workspace and download all of it',
    use: 'Only the owner downloads everything',
    status: 'shipped',
    reach: after(['propose_role', 'propose_role_change']),
    unexpressible: NO_DRAFT_ROLES,
  },
  'people.group.os': {
    name: 'Rights over OS',
    what: 'Who may ask OS, let it make changes, talk live and keep facts',
    use: 'Staff ask OS but cannot let it change the setup',
    status: 'shipped',
    reach: after(['propose_role', 'propose_role_change']),
    unexpressible: NO_DRAFT_ROLES,
  },
  'people.owner': {
    name: 'Owner',
    what: 'The person who makes the workspace, with every right',
    use: 'The shop owner',
    status: 'shipped',
    reach: made([]),
    example: SETUP_HOLDS_NAME,
  },
  'people.role': {
    name: 'Roles',
    what: 'A named set of rights; entry reach off, own, their team, everyone',
    use: 'Counter writes sales; Delivery sees home deliveries only',
    ask: 'Who else writes in it, and what should each of them see?',
    also: ['staff', 'worker', 'accountant', 'manager', 'naukar', 'munim'],
    status: 'shipped',
    reach: made(['propose_role', 'propose_role_change', 'propose_role_copy', 'propose_role_remove']),
    studioExample: withRole('people_role', 'Counter staff', []),
  },
  'people.access': {
    name: 'Access per record',
    what: 'Who may see or change a record, or one field of it',
    use: 'Hide the cost price from staff',
    status: 'shipped',
    reach: made(['propose_grant']),
    studioExample: withRole('people_access', 'Sales staff', [{ record: 'people_access_sales', level: 'see' }]),
  },
  'people.invite': {
    name: 'Invitations',
    what: 'Ask a person to join, by a link or a link and a code',
    use: 'Invite the accountant',
    also: ['invite', 'add staff', 'join'],
    status: 'shipped',
    reach: after(['propose_invite', 'propose_invites', 'propose_invite_change']),
    unexpressible: NO_DRAFT_ROLES,
  },
  'people.reports_to': {
    name: 'Reports to',
    what: 'Who is in whose team',
    use: 'Delivery staff report to the manager',
    status: 'shipped',
    reach: after(['propose_member_change']),
    unexpressible: NO_DRAFT_ROLES,
  },
  'people.handover': {
    name: 'Hand over',
    what: 'Make someone else the main admin of the workspace',
    use: 'The owner hands the shop to a son',
    status: 'shipped',
    reach: after(['propose_handover', 'propose_handover_answer']),
    unexpressible: NO_DRAFT_ROLES,
  },
  'people.ask_right': {
    name: 'Ask for a right',
    what: 'A member asks for more access, and the owner answers',
    use: 'Staff ask to see the totals',
    status: 'shipped',
    reach: after(['propose_ask', 'propose_ask_answer']),
    unexpressible: NO_DRAFT_ROLES,
  },

  /* --------------------------------------------------------- the memory */
  'memory.fact': {
    name: 'Facts',
    what: 'Sentences OS remembers about the business',
    use: 'We close on Tuesdays',
    status: 'shipped',
    reach: after(['propose_fact', 'propose_fact_change'], false),
    unexpressible: 'A setup draft holds parts, not facts; the setup conversation keeps them.',
  },
  'memory.setup_thread': {
    name: 'Setup conversation',
    what: 'The setup conversation, kept as the first thread of the workspace',
    use: 'Carry on where setup left off',
    status: 'shipped',
    reach: { setup: 'no', edit: 'no', os: [], screen: false },
    unexpressible: 'The conversation is kept beside the draft, never inside it.',
  },
  'memory.website': {
    name: 'Website',
    what: "The business's own website, read for its name, items, prices, phones and hours",
    use: 'Read the menu off the website',
    also: ['website', 'site'],
    status: 'shipped',
    reach: after(['read_website'], false),
    unexpressible: 'A website is read beside the draft, never inside it.',
  },
  'memory.unmet_need': {
    name: 'Noted wish',
    what: 'A note of something the parts cannot hold yet',
    use: 'They want to send customers reminders',
    status: 'shipped',
    reach: after(['note_unmet_need'], false),
    unexpressible: 'A wish is noted beside the draft, never drawn in it.',
  },

  /* ---------------------------------------------------------- the input */
  'input.file.sheet': {
    name: 'Spreadsheet',
    what: 'A spreadsheet file OS reads',
    use: 'A price list; last month of sales',
    also: ['sheet', 'xlsx', 'spreadsheet'],
    status: 'shipped',
    reach: after(['read_file'], false),
    unexpressible: 'A file is read beside the draft, never inside it.',
  },
  'input.file.csv': {
    name: 'CSV file',
    what: 'Comma-separated rows OS reads',
    use: 'An export from another tool',
    status: 'shipped',
    reach: after(['read_file'], false),
    unexpressible: 'A file is read beside the draft, never inside it.',
  },
  'input.file.tally': {
    name: 'Accounting export',
    what: 'An accounting export in XML that OS reads',
    use: 'Parties and ledgers from the accounts',
    also: ['xml', 'ledger', 'accounts'],
    status: 'shipped',
    reach: after(['read_file'], false),
    unexpressible: 'A file is read beside the draft, never inside it.',
  },
  'input.file.pdf': {
    name: 'PDF',
    what: 'A PDF OS reads',
    use: 'A printed rate card',
    status: 'shipped',
    reach: after(['read_file'], false),
    unexpressible: 'A file is read beside the draft, never inside it.',
  },
  'input.file.image': {
    name: 'Photo',
    what: 'A photo OS reads',
    use: 'A photo of a register page',
    also: ['photo', 'picture', 'register photo'],
    status: 'shipped',
    reach: after(['read_file'], false),
    unexpressible: 'A file is read beside the draft, never inside it.',
  },
  'input.file.website': {
    name: 'Website page',
    what: 'A page of a website, kept like a file',
    use: 'The menu page of the website',
    status: 'shipped',
    reach: after(['read_website'], false),
    unexpressible: 'A website is read beside the draft, never inside it.',
  },
  'input.file.xls': {
    name: 'Older spreadsheet',
    what: 'An older spreadsheet file (.xls) OS reads',
    use: 'Last year of sales from an old workbook',
    also: ['xls', 'old spreadsheet'],
    status: 'shipped',
    reach: after(['read_file'], false),
    unexpressible: 'A file is read beside the draft, never inside it.',
  },
  'input.file.docx': {
    name: 'Word document',
    what: 'A Word document OS reads',
    use: 'A price list typed in a document',
    also: ['docx', 'word', 'document'],
    status: 'shipped',
    reach: after(['read_file'], false),
    unexpressible: 'A file is read beside the draft, never inside it.',
  },
  'input.file.pptx': {
    name: 'PowerPoint',
    what: 'Slides OS reads',
    use: 'A rate card made as slides',
    also: ['pptx', 'slides'],
    status: 'shipped',
    reach: after(['read_file'], false),
    unexpressible: 'A file is read beside the draft, never inside it.',
  },
  'input.file.json': {
    name: 'JSON export',
    what: 'An export in JSON that OS reads',
    use: 'The data exported from another tool',
    also: ['json'],
    status: 'shipped',
    reach: after(['read_file'], false),
    unexpressible: 'A file is read beside the draft, never inside it.',
  },
  'input.file.xml': {
    name: 'XML export',
    what: 'An export in XML that OS reads',
    use: 'Items exported from another tool',
    also: ['xml'],
    status: 'shipped',
    reach: after(['read_file'], false),
    unexpressible: 'A file is read beside the draft, never inside it.',
  },
  'input.file.text': {
    name: 'Text',
    what: 'Plain text or tab-separated rows OS reads',
    use: 'A list pasted from a notes app',
    also: ['txt', 'tsv', 'text'],
    status: 'shipped',
    reach: after(['read_file'], false),
    unexpressible: 'A file is read beside the draft, never inside it.',
  },
  'input.file.place': {
    name: 'Map place',
    what: 'A place on a map, kept like a file',
    use: 'The shop on the map',
    also: ['map', 'place'],
    status: 'planned',
    reach: nothing,
    unexpressible: 'A file is read beside the draft, never inside it.',
    cannotYet: { need: 'Keep a map place as a source', fit: 'say the address and hours in the conversation' },
  },
  'input.file.page': {
    name: 'Web page',
    what: 'One public page, kept like a file',
    use: 'The business page of the shop',
    also: ['page', 'link'],
    status: 'planned',
    reach: nothing,
    unexpressible: 'A file is read beside the draft, never inside it.',
    cannotYet: { need: 'Keep one public page as a source', fit: 'say what the page shows in the conversation' },
  },
  'input.import': {
    name: 'Import',
    what: "A file's rows written into records by a job",
    use: 'Three hundred customers from a spreadsheet',
    also: ['import', 'upload', 'data'],
    status: 'shipped',
    reach: made(['propose_import']),
    studioExample: {
      records: [list('input_import_customers', 'Customers')],
      sources: [{ id: IMPORT_SOURCE, role: 'mine', dismissed: false }],
      imports: [
        {
          key: 'input_import',
          source: IMPORT_SOURCE,
          sheet: 'Customers',
          record: 'input_import_customers',
          headerRow: 0,
          skipRows: [],
          columns: [{ column: 0, header: 'Name', field: 'name', transform: { kind: 'text' } }],
        },
      ],
    },
  },
  'input.entries_from_file': {
    name: 'Entries from a file',
    what: 'A few rows of a file written as entries',
    use: "Last week's sales from a photo",
    status: 'shipped',
    reach: after(['propose_entries'], false),
    unexpressible: 'A setup never writes an entry.',
  },
  'input.web_search': {
    name: 'Web search',
    what: 'OS looks up how a trade works',
    use: 'How a dairy pays its farmers',
    status: 'shipped',
    reach: { setup: 'no', edit: 'no', os: [], screen: false },
    unexpressible: 'Research happens beside the draft, never inside it.',
  },
  'input.place': {
    name: 'Place lookup',
    what: "A business's place read from a map link",
    use: 'The address and hours of the shop',
    status: 'planned',
    reach: nothing,
    unexpressible: NO_DRAFT_PLACE,
    cannotYet: { need: 'Read a place from a map link', fit: 'ask the person for the address and hours' },
  },
  'input.app_research': {
    name: 'Looking up the app you use',
    what: 'OS reads how the app the person uses now works',
    use: 'Set up the same records as the app they use',
    status: 'planned',
    reach: nothing,
    unexpressible: NO_DRAFT_PLACE,
    cannotYet: { need: 'Look up how the app you use works', fit: 'ask what they keep in it today, record by record' },
  },

  /* ------------------------------------------------------- not possible yet */
  'not_yet.send_messages': {
    name: 'Send messages',
    what: 'Messages or reminders sent to customers',
    use: 'Remind customers who owe',
    also: ['message', 'sms', 'reminder', 'bhejna', 'bhejo', 'customers', 'remind'],
    status: 'not_in_recordos',
    reach: nothing,
    unexpressible: 'No part sends anything outside the app.',
    cannotYet: { need: 'Send messages to customers', fit: 'a Due date on the entry and a card of what is over 30 days' },
  },
  'not_yet.barcode_scanning': {
    name: 'Barcode scanning',
    what: 'Items found by scanning their barcode at checkout or stock in',
    use: 'Scan items into a bill',
    also: ['barcode', 'scan', 'scanner', 'scanning', 'qr', 'sku'],
    status: 'not_in_recordos',
    reach: nothing,
    unexpressible: 'No part reads a barcode or a scanner.',
    cannotYet: { need: 'Barcode scanning', fit: 'pick the item from the Items list by name, its usual rate filled in' },
  },
  'not_yet.gst_filing': {
    name: 'GST filing',
    what: 'Tax returns filed from the entries',
    use: 'File GST each month',
    also: ['gst', 'tax', 'return', 'filing'],
    status: 'not_in_recordos',
    reach: nothing,
    unexpressible: 'No part files anything with the government.',
    cannotYet: { need: 'GST filing', fit: 'a GST field and a monthly tax card' },
  },
  'not_yet.payroll_payment': {
    name: 'Pay salaries',
    what: 'Salaries paid out from the app',
    use: 'Pay staff at the month end',
    also: ['salary', 'payroll', 'tankhwah', 'pagar'],
    status: 'not_in_recordos',
    reach: nothing,
    unexpressible: 'No part moves money.',
    cannotYet: { need: 'Payroll payments', fit: 'attendance and a salary card' },
  },
  'not_yet.bank_feed': {
    name: 'Bank feed',
    what: 'Bank entries arriving by themselves',
    use: 'Every deposit shows up on its own',
    also: ['bank', 'statement', 'passbook'],
    status: 'not_in_recordos',
    reach: nothing,
    unexpressible: 'No part connects to a bank.',
    cannotYet: { need: 'A bank feed', fit: 'a Bank deposits record entered by hand or imported from a statement file' },
  },
  'not_yet.stock_from_lines': {
    name: 'Stock from bill lines',
    what: 'Stock falling as each line of a bill is saved',
    use: 'Stock down for every item sold',
    also: ['stock', 'inventory', 'maal', 'godown'],
    status: 'not_in_recordos',
    reach: nothing,
    unexpressible: 'Stock moves by a quantity rule, and only for lines that pick an item from a list.',
    cannotYet: { need: "Stock falling as a bill's lines are saved", fit: 'a card of stock received beside items sold' },
  },
  'not_yet.settle_against_bills': {
    name: 'Settle against bills',
    what: 'A payment set against particular bills',
    use: 'This payment clears bills 12 and 14',
    also: ['udhaar', 'khata', 'baki', 'settle'],
    status: 'not_in_recordos',
    reach: nothing,
    unexpressible: 'A payment is set against the oldest open charge, never against bills a person picks.',
    cannotYet: {
      need: 'Settle a payment against particular bills',
      fit: "a payment received, set against the customer's oldest open charge by itself, and moved to another charge on their statement",
    },
  },
  'not_yet.third_lines_level': {
    name: 'A third level of lines',
    what: 'Lines inside lines inside lines',
    use: 'Rooms of a stay, guests of a room, meals of a guest',
    status: 'not_in_recordos',
    reach: nothing,
    attempt: one(
      rec('third_lines', 'Stays', [
        f('rooms', 'Rooms', 'lines', {
          lines: {
            noun: 'room',
            nouns: 'rooms',
            fields: [
              f('room', 'Room', 'text'),
              f('guests', 'Guests', 'lines', {
                lines: {
                  noun: 'guest',
                  nouns: 'guests',
                  fields: [
                    f('guest', 'Guest', 'text'),
                    f('meals', 'Meals', 'lines', { lines: { noun: 'meal', nouns: 'meals', fields: [f('meal', 'Meal', 'text')] } }),
                  ],
                },
              }),
            ],
          },
        }),
      ]),
    ),
    cannotYet: { need: 'A third level of lines', fit: 'a separate record that picks the second level' },
  },
} satisfies Record<CapabilityId, CapabilityWords>;
